import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function SignIn() {
  const navigate = useNavigate();
  const [authError, setAuthError] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const [showSignUpLink, setShowSignUpLink] = React.useState(false);

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateInputs()) return;
    setAuthError('');
    setShowSignUpLink(false);

    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    const password = data.get('password') as string;

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db, 'users', cred.user.uid));
      navigate(snap.exists() ? '/' : '/onboarding');
    } catch (error: any) {
      if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/user-not-found'
      ) {
        setAuthError('No account found with those credentials.');
        setShowSignUpLink(true);
      } else {
        setAuthError('Sign-in failed. Please try again.');
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100dvh - 72px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0d0d0c',
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography component="h1" variant="h4">Sign in</Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl>
            <FormLabel htmlFor="email">Email</FormLabel>
            <TextField
              error={emailError}
              helperText={emailErrorMessage}
              id="email"
              type="email"
              name="email"
              placeholder="your@email.com"
              autoComplete="email"
              autoFocus
              required
              fullWidth
              variant="outlined"
              color={emailError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="password">Password</FormLabel>
            <TextField
              error={passwordError}
              helperText={passwordErrorMessage}
              name="password"
              placeholder="••••••"
              type="password"
              id="password"
              autoComplete="current-password"
              required
              fullWidth
              variant="outlined"
              color={passwordError ? 'error' : 'primary'}
            />
          </FormControl>
          {authError && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography color="error">{authError}</Typography>
              {showSignUpLink && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Don't have an account?{' '}
                  <Link to="/SignUp" style={{ color: '#d0d1c5' }}>Sign up for free</Link>
                </Typography>
              )}
            </Box>
          )}
          <Button type="submit" fullWidth variant="contained" onClick={validateInputs}>
            Sign in
          </Button>
        </Box>

        <Divider>or</Divider>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <GoogleSignInButton
            label="Sign in with Google"
            onSuccess={async () => {
              const user = auth.currentUser!;
              const snap = await getDoc(doc(db, 'users', user.uid));
              if (!snap.exists()) {
                await signOut(auth);
                throw new Error('no-account');
              }
              navigate('/');
            }}
            onError={(error) => {
              if (error.message === 'no-account') {
                setAuthError('No account found for this Google email. Please sign up first.');
              } else {
                setAuthError('Google sign-in failed. Please try again.');
              }
            }}
          />
          <Typography sx={{ textAlign: 'center' }}>
            Not a current member?{' '}
            <Link to="/SignUp" style={{ color: '#d0d1c5' }}>Sign Up For Free</Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
